import type { SectionProps } from "../context";
import { field } from "../context";
import { CARD_HOVER, Container, EditorImagePlaceholder, Img, MultiLine, OrderLink, Price, STACK, STAGGER, SectionHeader, SectionShell, cx } from "../ui";

type ProductItem = SectionProps<"products">["node"]["content"]["items"][number];

function statusLabel(ctx: SectionProps<"products">["ctx"], status: ProductItem["status"]) {
  const en = ctx.site.locale === "en-US";
  if (status === "sold") return en ? "Sold" : "Terjual";
  if (status === "reserved") return en ? "Reserved" : "Dipesan";
  return null;
}

/** Artist under the title (artworks, handmade goods). */
function Artist({ ctx, item, p, className }: { ctx: SectionProps<"products">["ctx"]; item: ProductItem; p: string; className?: string }) {
  if (!item.artist) return null;
  return (
    <p className={cx("text-sm text-site-muted", className)} {...field(ctx, `${p}.artist`)}>
      {item.artist}
    </p>
  );
}

/** Medium · size · year: a spec list on cards, one compact line on the grid. */
function Specs({ ctx, item, p, compact }: { ctx: SectionProps<"products">["ctx"]; item: ProductItem; p: string; compact?: boolean }) {
  const en = ctx.site.locale === "en-US";
  const rows = (
    [
      ["medium", en ? "Medium" : "Media", item.medium],
      ["size", en ? "Size" : "Ukuran", item.size],
      ["year", en ? "Year" : "Tahun", item.year],
    ] as const
  ).filter(([, , v]) => !!v);
  if (!rows.length) return null;
  if (compact) {
    return (
      <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-site-muted">
        {rows.map(([k, , v], i) => (
          <span key={k}>
            {i ? " · " : ""}
            <span {...field(ctx, `${p}.${k}`)}>{v}</span>
          </span>
        ))}
      </p>
    );
  }
  return (
    <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 border-t border-site-border pt-4 text-sm">
      {rows.map(([k, label, v]) => (
        <div key={k} className="contents">
          <dt className="text-site-muted">{label}</dt>
          <dd className="text-site-text" {...field(ctx, `${p}.${k}`)}>
            {v}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function Products({ node, ctx }: SectionProps<"products">) {
  const c = node.content;
  const cards = node.variant === "cards";
  // Without any photos, a row of empty squares looks broken: render compact text cards instead.
  const showMedia = ctx.editor || c.items.some((item) => !!item.image);
  return (
    <SectionShell ctx={ctx} node={node}>
      <Container>
        <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} description={c.description} />
        <ul className={cx(STACK, STAGGER, cards ? "grid gap-5 @2xl:grid-cols-2 @5xl:grid-cols-3 @3xl:gap-6" : "grid grid-cols-2 gap-x-4 gap-y-8 @3xl:grid-cols-3 @3xl:gap-x-6 @5xl:grid-cols-4")}>
          {c.items.map((item, i) => {
            const p = `content.items.${i}`;
            const status = statusLabel(ctx, item.status);
            const sold = item.status === "sold";
            const priceClass = sold ? "line-through decoration-1 opacity-60" : "";
            const media = !showMedia ? null : (
              <div className={cx("relative overflow-hidden bg-site-surface", cards ? "aspect-[4/3]" : "aspect-square rounded-site-md")}>
                {item.image ? (
                  <Img
                    ctx={ctx}
                    image={item.image}
                    sizes={cards ? "(min-width: 1024px) 360px, (min-width: 672px) 50vw, 100vw" : "(min-width: 1024px) 280px, (min-width: 768px) 33vw, 50vw"}
                    path={`${p}.image`}
                    className="transition-transform duration-500 group-hover:scale-[1.03] motion-reduce:transition-none"
                  />
                ) : ctx.editor ? (
                  <EditorImagePlaceholder ctx={ctx} path={`${p}.image`} />
                ) : (
                  <div aria-hidden="true" className="grid h-full w-full place-items-center bg-site-primary/5 text-4xl text-site-primary/30 baze-heading">
                    {item.name.slice(0, 1)}
                  </div>
                )}
                {item.badge || status ? (
                  <span className="absolute top-3 left-3 flex flex-wrap gap-1.5">
                    {status ? (
                      <span className={cx("rounded-full px-2.5 py-1 text-xs font-semibold shadow-site-sm", sold ? "bg-site-inverted text-site-on-inverted" : "bg-site-surface text-site-text")}>{status}</span>
                    ) : null}
                    {item.badge ? (
                      <span className="rounded-full bg-site-primary px-2.5 py-1 text-xs font-semibold text-site-on-primary shadow-site-sm" {...field(ctx, `${p}.badge`)}>
                        {item.badge}
                      </span>
                    ) : null}
                  </span>
                ) : null}
              </div>
            );
            if (cards) {
              return (
                <li key={i} className={cx("group flex flex-col overflow-hidden rounded-site-md border border-site-border bg-site-surface shadow-site-sm", CARD_HOVER)}>
                  {media}
                  <div className="flex flex-1 flex-col p-5 @3xl:p-6">
                    {!showMedia && (item.badge || status) ? (
                      <span className="mb-3 flex flex-wrap gap-1.5">
                        {status ? <span className="rounded-full bg-site-inverted px-2.5 py-1 text-xs font-semibold text-site-on-inverted">{status}</span> : null}
                        {item.badge ? (
                          <span className="rounded-full bg-site-primary px-2.5 py-1 text-xs font-semibold text-site-on-primary" {...field(ctx, `${p}.badge`)}>
                            {item.badge}
                          </span>
                        ) : null}
                      </span>
                    ) : null}
                    <h3 className="baze-heading text-xl leading-snug" {...field(ctx, `${p}.name`)}>
                      {item.name}
                    </h3>
                    <Artist ctx={ctx} item={item} p={p} className="mt-1" />
                    <MultiLine ctx={ctx} text={item.description} path={`${p}.description`} className="mt-2 text-sm leading-relaxed text-site-muted" />
                    <Specs ctx={ctx} item={item} p={p} />
                    <div className="mt-auto flex flex-wrap items-center justify-between gap-3 pt-5">
                      <Price value={item.price} note={item.priceNote} className={cx("text-lg text-site-text", priceClass)} />
                      {sold ? null : <OrderLink
                        ctx={ctx}
                        message={c.orderMessage}
                        produk={item.name}
                        className="inline-flex min-h-11 items-center gap-2 rounded-site-button bg-site-primary px-4 text-sm font-semibold text-site-on-primary shadow-site-sm transition hover:shadow-site-md"
                      />}
                    </div>
                  </div>
                </li>
              );
            }
            return (
              <li key={i} className={cx("group flex flex-col", !showMedia && "rounded-site-md border border-site-border bg-site-surface p-5")}>
                {media}
                {!showMedia && (item.badge || status) ? (
                  <span className="mb-3 flex flex-wrap gap-1.5">
                    {status ? <span className="rounded-full bg-site-inverted px-2.5 py-1 text-xs font-semibold text-site-on-inverted">{status}</span> : null}
                    {item.badge ? (
                      <span className="rounded-full bg-site-primary px-2.5 py-1 text-xs font-semibold text-site-on-primary" {...field(ctx, `${p}.badge`)}>
                        {item.badge}
                      </span>
                    ) : null}
                  </span>
                ) : null}
                <h3 className="mt-3 text-[0.9375rem] leading-snug font-semibold text-site-text @3xl:text-base" {...field(ctx, `${p}.name`)}>
                  {item.name}
                </h3>
                <Artist ctx={ctx} item={item} p={p} className="mt-0.5" />
                <Specs ctx={ctx} item={item} p={p} compact />
                <Price value={item.price} note={item.priceNote} className={cx("mt-1 text-site-text", priceClass)} />
                {sold ? null : (
                  <OrderLink
                    ctx={ctx}
                    message={c.orderMessage}
                    produk={item.name}
                    className="mt-3 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-site-button border border-site-border text-sm font-semibold text-site-text transition hover:border-site-primary hover:bg-site-primary hover:text-site-on-primary"
                  />
                )}
              </li>
            );
          })}
        </ul>
      </Container>
    </SectionShell>
  );
}
